import React, { useCallback, useEffect, useRef } from 'react';
import { BackButton, Input, ErrorAlert, Button, ErrorMessage, Select } from '../Components/Simple';
import useAsyncPricerApi from '../Hooks/useAsyncPricerApi';
import { ApiError, EntryTable, EntryTableColumn } from '../Components/Combined';
import { API_DATE_STRING_FORMAT, DATE_STRING_FORMAT } from '../Constants/stringFormats';
import { PFS as modelType } from '../Constants/modelTypes';
import useCalendarYear from '../Hooks/useCalendarYear';
import useSessionState from '../Hooks/useSessionState';
import { useNavigate, useLocation } from 'react-router-dom';
import useCollection from '../Hooks/useCollection';
import useExportRequest from '../Hooks/useExportRequest';
import useImportRequest from '../Hooks/useImportRequest';
import { formatDateStringIfMatches, formatStringAsCurrency, getDefaultDateString } from '../Utils/stringUtils';
import { validateBilled, validateLineServiceDate, validateZipCode } from '../Utils/validation';
import FilePickerButton from '../Components/Simple/FilePickerButton';
import { mapToSetters } from '../Utils/mapping';
import { transformNumberToOneDecimalPlace, transformStringToUpCase } from '../Utils/valueTransforms';
import ValidatedInput from '../Components/Combined/ValidatedInput';
import { enable5ZipWarning, enableAsyncPricingApi } from '../Utils/features';
import prepareServiceLines from '../Utils/prepareServiceLines';
import usePricerApi from '../Hooks/usePricerApi';
import { EXECUTE, INFORMATIONAL } from '../Constants/serviceMode';
import { serviceModeValues } from '../Constants/dropdownValues';

export default function PFS() {
    const location = useLocation();
    const { prePopulatedProviderNPI, prePopulatedPlaceOfService, prePopulatedSpecialtyCode } = location.state || {};
    const [claimId, setClaimId] = useSessionState('', 'claimId');
    const [providerNPI, setProviderNPI] = useSessionState(prePopulatedProviderNPI ?? '', 'providerNPI');
    const [renderingProviderZipCode, setRenderingProviderZipCode] = useSessionState('', 'zipCode');
    const [placeOfServiceCode, setPlaceOfServiceCode] = useSessionState(prePopulatedPlaceOfService ?? '', 'placeOfService');
    const [specialtyCode, setSpecialtyCode] = useSessionState(prePopulatedSpecialtyCode ?? '', 'specialtyCode');
    const [fromDate, setFromDate] = useSessionState(getDefaultDateString(), 'fromDate');
    const [toDate, setToDate] = useSessionState(getDefaultDateString(), 'toDate');
    const [calendarYear, setCalendarYearInputDate] = useCalendarYear(null);
    const [ptpAddOnServiceMode, setPtpAddOnServiceMode] = useSessionState('', 'ptpAddOnServiceMode');

    const navigate = useNavigate();

    const getNewLine = (index) => {
        return {
            lineId: index + 1,
            procedureCode: '',
            modifierOne: '',
            modifierTwo: '',
            serviceDate: '',
            units: 0,
            billed: ''
        };
    };

    const getDefaultLines = () => {
        return [getNewLine(0), getNewLine(1), getNewLine(2), getNewLine(3), getNewLine(4)];
    };

    const linesCollection = useCollection(getDefaultLines(), getNewLine, 'lines');

    const prepareLines = linesCollection => prepareServiceLines(linesCollection, line => line.procedureCode);

    const pricer = enableAsyncPricingApi ? useAsyncPricerApi(modelType) : usePricerApi(modelType);


    const [fileExporting, _fileExportSuccess, fileExportError, fileExportExecute, fileExportReset] = useExportRequest(modelType, {
        "claimId": claimId,
        "providerNPI": providerNPI,
        "renderingProviderZipCode": renderingProviderZipCode,
        "placeOfServiceCode": placeOfServiceCode,
        "specialtyCode": specialtyCode,
        "fromDate": fromDate,
        "toDate": toDate,
        "lines": linesCollection.values,
        ptpAddOnServiceMode
    });

    const handleFileSave = () => {
        pricer.reset();
        fileExportExecute();
    };

    const [fileImporting, fileImportSuccess, fileImportError, fileImportResult, fileImportReset, fileImportOnChange] = useImportRequest(modelType);

    const handleFileImportOnChange = (e) => {
        pricer.reset();
        fileImportOnChange(e);
    };

    const handleSubmit = async () => {
        fileExportReset();
        fileImportReset();

        if (enableAsyncPricingApi) {
            await pricer.execute(formatRequest());
        } else {
            await pricer.execute(calendarYear, formatRequest());
        }
    };

    const formatRequest = () => {
        return {
            claimId: claimId || null,
            providerName: providerNPI || null,
            pos: placeOfServiceCode || null,
            specialtyCode: specialtyCode || null,
            zipCode: renderingProviderZipCode || null,
            fromServiceDate: formatDateStringIfMatches(fromDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            toServiceDate: formatDateStringIfMatches(toDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            ptpAddOnServiceMode,
            includeCmsExtendedRvus: "Y",
            mue: "N",
            maxProcs: "Y",
            serviceLineItems: prepareLines(linesCollection)
        };
    }

    const handleReset = () => {
        setClaimId('');
        setProviderNPI('');
        setRenderingProviderZipCode('');
        setPlaceOfServiceCode('');
        setSpecialtyCode('');
        setFromDate(getDefaultDateString());
        setToDate(getDefaultDateString());
        linesCollection.setValues(getDefaultLines());
        fileImportReset();
        fileExportReset();
        setPtpAddOnServiceMode(EXECUTE);
        pricer.reset();
    };

    useEffect(() => {
        if (fileImportSuccess) {
            const setters = {
                claimId: setClaimId,
                providerNPI: setProviderNPI,
                renderingProviderZipCode: setRenderingProviderZipCode,
                placeOfServiceCode: setPlaceOfServiceCode,
                specialtyCode: setSpecialtyCode,
                fromDate: setFromDate,
                toDate: setToDate,
                lines: linesCollection.setValues,
                ptpAddOnServiceMode: setPtpAddOnServiceMode
            };
            handleReset();
            mapToSetters(fileImportResult, setters);
        }
    }, [fileImportSuccess]);

    useEffect(() => {
        setCalendarYearInputDate(fromDate);
    }, [fromDate]);

    useEffect(() => {
        if (pricer.success) {
            navigate('result', {
                state: {
                    calendarYear,
                    result: pricer.result,
                    lines: prepareLines(linesCollection),
                    ptpAddOnServiceMode
                }
            });
        }
    }, [pricer.success]);

    const handleValidateZipCode = useCallback((v) => validateZipCode(v, calendarYear), [calendarYear]);

    return (
        <div className="py-2">
            <div className="row my-2 justify-content-between sticky top g-0 border-bottom align-items-center">
                <div className="col-auto">
                    <h3>Professional Pricer</h3>
                    <div className="fs-6">
                        Enter all claim information requested below. Please press SUBMIT when complete to calculate the prospective payment amount.
                    </div>
                </div>
                <div className="col-auto">
                    <BackButton />
                </div>
            </div>
            {(pricer.error || fileImportError || fileExportError) && (<div>
                <ApiError error={pricer.error} />
                {fileImportError ? <ErrorMessage errors={[{ message: fileImportError }]} /> : null}
                {fileExportError ? <ErrorMessage errors={[{ message: fileExportError }]} /> : null}
            </div>)}
            <form noValidate={true}>
                <div className="row my-2">
                    <div className="col-md-4">
                        <Input id="claim-id" labelClassName="fw-bold" label="Claim ID" onChange={setClaimId} value={claimId} />
                    </div>
                    <div className="col-md-4">
                        <Input label="Provider NPI" labelClassName="fw-bold" id="provider-npi" onChange={setProviderNPI} value={providerNPI} maxLength={10} />
                    </div>
                </div>
                <div className="row my-2">
                    <div className="col-md-4">
                        {enable5ZipWarning && <ValidatedInput label="Service Facility Zip Code" maxLength={9} labelClassName="fw-bold" id="rendering-provider-zip-code" onChange={setRenderingProviderZipCode} value={renderingProviderZipCode} onValidate={handleValidateZipCode} />}
                        {!enable5ZipWarning && <Input label="Service Facility Zip Code" maxLength={9} labelClassName="fw-bold" id="rendering-provider-zip-code" onChange={setRenderingProviderZipCode} value={renderingProviderZipCode} />}
                    </div>
                    <div className="col-md-4">
                        <Input label="Place of Service Code" labelClassName="fw-bold" id="place-of-service-code" onChange={setPlaceOfServiceCode} value={placeOfServiceCode} />
                    </div>
                    <div className="col-md-4">
                        <Input label="Specialty Code" labelClassName="fw-bold" id="specialty-code" onChange={setSpecialtyCode} value={specialtyCode} />
                    </div>
                </div>
                <div className="row my-2">
                    <div className="col-md-4">
                        <Input label="From Date" preservePlaceholder={true} labelClassName="fw-bold" id="from-date" type="date" onChange={setFromDate} value={fromDate} placeholder={DATE_STRING_FORMAT.toUpperCase()} />
                    </div>
                    <div className="col-md-4">
                        <Input label="To Date" preservePlaceholder={true} labelClassName="fw-bold" id="to-date" type="date" onChange={setToDate} value={toDate} placeholder={DATE_STRING_FORMAT.toUpperCase()} />
                    </div>
                    <div className="col-md-4 align-self-center mt-4 fw-bold">
                        {calendarYear && <>Calendar Year {calendarYear}</>}
                    </div>
                </div>
                {enableAsyncPricingApi && <div className="row my-2">
                    <div className="col-md-4">
                        <Select id="ptpaddon-service-mode" value={ptpAddOnServiceMode} labelClassName="fw-bold" onChange={setPtpAddOnServiceMode} label="Apply PTP/Add On Edits">
                            {serviceModeValues.filter(v => (v.value != INFORMATIONAL)).map((v, i) => <option key={i} value={v.value}>{v.text}</option>)}
                        </Select>
                    </div>
                </div>}
                <hr />
                <div className="row g-0">
                    <div className="col my-2">
                        <EntryTable id="service-line-items"
                            collection={linesCollection}
                            lineCountLabel="Total Lines"
                            disableMaxWarningMessage={true}
                            upDownNavigation={true}
                            max={100}
                            runningTotal={{
                                name: 'billed',
                                className: 'me-2',
                                formatter: formatStringAsCurrency
                            }}>
                            <EntryTableColumn label="Line ID"
                                name="lineId"
                                type="label"
                                className="mw-50" />
                            <EntryTableColumn label="Procedure Code (CPT/HCPCS)"
                                name="procedureCode"
                                type="text"
                                valueTransform={transformStringToUpCase} />
                            <EntryTableColumn label="Modifier 1"
                                name="modifierOne"
                                type="text"
                                valueTransform={transformStringToUpCase} />
                            <EntryTableColumn label="Modifier 2"
                                name="modifierTwo"
                                type="text"
                                valueTransform={transformStringToUpCase} />
                            <EntryTableColumn label="Service Date"
                                name="serviceDate"
                                type="date"
                                className="mw-125"
                                placeholder={DATE_STRING_FORMAT.toUpperCase()}
                                onValidate={(value) => validateLineServiceDate(value, fromDate, toDate)} />
                            <EntryTableColumn label="Units"
                                name="units"
                                type="number"
                                className="mw-75"
                                valueTransform={transformNumberToOneDecimalPlace} />
                            <EntryTableColumn label="Billed"
                                name="billed"
                                type="text"
                                className="mw-100 text-end"
                                onValidate={validateBilled} />
                        </EntryTable>
                    </div>
                </div>
                <div className="row justify-content-between my-2 sticky bottom g-0 border-top">
                    <div className="col-auto align-self-center">
                        <div className="row g-0">
                            <div className="col-auto ms-2">
                                <Button className="btn-outline-secondary" id="save-request"
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    onClick={handleFileSave}
                                    text="Save" />
                            </div>
                            <div className="col-auto ms-2">
                                <FilePickerButton className="btn-outline-secondary" id="load-request"
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    onChange={handleFileImportOnChange}
                                    loading={fileImporting}
                                    text="Load"
                                    accepts=".mrpreq" />
                            </div>
                        </div>
                    </div>
                    <div className="col-auto align-self-center">
                        <ErrorAlert visible={pricer.error || fileImportError || fileExportError} />
                    </div>
                    <div className="col-auto align-self-center">
                        <div className="row g-0 justify-cotent-end">
                            <div className="col-auto">
                                <Button className="btn-outline-secondary" id="reset-button"
                                    onClick={handleReset}
                                    disabled={pricer.loading}
                                    text="Reset" />
                            </div>
                            <div className="col-auto ms-2">
                                <Button className="btn-primary" id="submit-button"
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    loading={pricer.loading}
                                    onClick={handleSubmit}
                                    text="Submit" />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
